import { useParams, useLocation, Navigate, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import ThemeProvider from '@/components/theme-provider'
import { BrainLauncher } from '@/components/brain/BrainLauncher'
import { BrainPanel } from '@/components/brain/BrainPanel'
import { PanelErrorBoundary } from '@/components/brain/PanelErrorBoundary'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { useBrainConversation } from '@/hooks/use-brain-conversation'
import { useFileDrop } from '@/hooks/use-file-drop'
import { useFitsSideBySide } from '@/hooks/use-mobile'
import { useFilePicker } from '@/hooks/use-file-picker'
import { sectionFromRoute } from '@/lib/where-i-am'
import { GuidedUpload } from '@/contexts/guided-upload'
import { WhereIAmProvider } from '@/contexts/WhereIAmContext'
import { useFocus } from '@/contexts/where-i-am'
import { Sidebar } from './Sidebar'

export function LocaleLayout() {
  // El proveedor envuelve al resto para que cualquier pantalla pueda declarar
  // qué tiene delante (3.5). Va aquí y no más adentro porque quien lee lo
  // declarado —la conversación— también vive aquí.
  return (
    <WhereIAmProvider>
      <Armazon />
    </WhereIAmProvider>
  )
}

function Armazon() {
  const { lang } = useParams<{ lang: string }>()
  const { t, i18n } = useTranslation()
  // **El panel abierto se recuerda aquí y no dentro de una página** (D7, 2.1).
  // Este componente es la ruta padre: sobrevive a cambiar de pantalla, así que
  // el panel no se cierra solo al navegar. Guardado dentro de una página se
  // destruiría en cada cambio, que es justo lo que este plan quita.
  const [brainAbierto, setBrainAbierto] = useState(false)
  // Y **dónde** se pone depende del sitio que haya, no del aparato (2.5, 2.6).
  const cabeAlLado = useFitsSideBySide()
  const cerrar = () => setBrainAbierto(false)
  // **La conversación vive aquí, no dentro del panel** (D7, 2.8). El panel se
  // desmonta tres veces: al cerrarlo, al cambiar de página y al cruzar los
  // 1.280 px, donde pasa de ir al lado a ir encima. Guardada dentro, se
  // perdería en las tres. Arranca cuando se abre por primera vez, para no
  // pedirle nada al servidor a quien no lo use.
  // Dónde está el usuario, sacado de la ruta (3.1, D2). Cambia al navegar sin
  // que la conversación se entere de nada más: lo lee en el turno siguiente.
  const { pathname } = useLocation()
  const focus = useFocus()
  // El idioma va con el sitio (5.1, D3): **el de la aplicacion decide el de la
  // respuesta**, no el del mensaje. Sale de la ruta, que es donde esa persona
  // ya lo eligio, y no del texto que escriba: un nombre propio o una frase
  // corta le harian cambiar de idioma a mitad de conversacion.
  const donde = useMemo(
    () => ({
      pagina: sectionFromRoute(pathname),
      ...(focus ? { sobre: focus } : {}),
      ...(lang ? { idioma: lang } : {}),
    }),
    [pathname, focus, lang],
  )
  const conversacion = useBrainConversation({ activo: brainAbierto, donde })
  // Soltar un archivo **abre el panel** y lo sube por la API de siempre (5.1,
  // D4). El Brain no puede llevarlo: acompaña y propone, que es lo que
  // `on_upload` ya hace en el servidor.
  const takeFile = useCallback(
    (archivo: File) => {
      setBrainAbierto(true)
      void conversacion.subirArchivo(archivo)
    },
    [conversacion],
  )
  const arrastrando = useFileDrop(takeFile)

  // **La otra puerta al mismo sitio** (3.1, R1). «Subir Documento» abre el
  // selector del sistema, y lo que se elija entra por donde ya entraba lo que
  // se suelta encima. El campo vive aqui y no en la pantalla porque quien sabe
  // que hacer con el archivo es el panel, que tambien vive aqui.
  const { open: askForFile, picker } = useFilePicker(takeFile, t('knowledge.upload_document'))
  const guidedUpload = useMemo(() => ({ askForFile }), [askForFile])
  
  // Cambiar idioma automáticamente cuando cambie la URL
  useEffect(() => {
    if (lang && ['en', 'es'].includes(lang)) {
      void i18n.changeLanguage(lang)
    }
  }, [lang, i18n])
  
  // Si el idioma no es válido, redirigir a /en
  if (lang && !['en', 'es'].includes(lang)) {
    return <Navigate to="/en" replace />
  }
  
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem disableTransitionOnChange>
      <GuidedUpload.Provider value={guidedUpload}>
      {/* El limite de fallo envuelve al panel en los dos sitios donde se pinta
          (6.1, D6). Va aqui y no dentro de `BrainPanel`: un limite dentro de lo
          que protege no sirve, porque se desmonta con ello. */}
      <Sidebar
        panel={
          brainAbierto && cabeAlLado ? (
            <PanelErrorBoundary onCerrar={cerrar}>
              <BrainPanel conversacion={conversacion} onCerrar={cerrar} />
            </PanelErrorBoundary>
          ) : null
        }
      >
        <Outlet />
      </Sidebar>

      {/* Cuando no cabe al lado va encima, que ahí sí es lo correcto: tapar
          media pantalla es peor que taparla entera, porque lo que queda no
          sirve. `sinCerrar` porque el panel ya trae su salida. */}
      {!cabeAlLado && (
        <Sheet open={brainAbierto} onOpenChange={setBrainAbierto}>
          <SheetContent side="right" sinCerrar className="w-full p-0 sm:max-w-md">
            <SheetTitle className="sr-only">{t('brain.title')}</SheetTitle>
            <PanelErrorBoundary onCerrar={cerrar}>
              <BrainPanel conversacion={conversacion} onCerrar={cerrar} />
            </PanelErrorBoundary>
          </SheetContent>
        </Sheet>
      )}

      {/* Fuera del `Sidebar` a propósito: el botón no pertenece a la
          navegación, y meterlo dentro lo ataría al ancho de la columna. Y solo
          mientras está cerrado: con el panel abierto había dos controles
          llamados «Cerrar el Brain» a la vez. */}
      {!brainAbierto && <BrainLauncher onAbrir={() => setBrainAbierto(true)} />}

      {/* Que se vea que la aplicacion acepta el archivo. Sin esto, soltarlo es
          una apuesta: no hay nada que diga que va a pasar algo. */}
      {arrastrando && (
        // No es un modal: es un velo que solo se ve mientras el puntero
        // arrastra un archivo, no atrapa el puntero (`pointer-events-none`) y
        // se va solo al soltar o al salir. Un Dialog aqui robaria el foco en
        // mitad de un arrastre y cancelaria el gesto, que es justo lo que hay
        // que dejar terminar.
        // eslint-disable-next-line no-restricted-syntax -- velo, no modal
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-background/80">
          <p className="text-lg font-semibold">{t('brain.archivo.soltar')}</p>
        </div>
      )}

      {/* El selector del sistema, oculto (`useFilePicker`). */}
      {picker}
      </GuidedUpload.Provider>
    </ThemeProvider>
  )
}
